import { ref, remove, set } from 'firebase/database';
import { ParentContext } from 'global/cnr/contexts/ParentContext';
import { useAppUserAccess } from 'global/cnr/contexts/useHelpers/useAppUserAccess';
import _ from 'lodash';
import { useCallback, useContext, useState } from 'react';
import { SportsSeasonContext } from '../cnr/contexts/SportsSeasonContext';
import { gameScoringTypes } from '../cnr/reducers/SportsSeasonReducer';
import { realTimeScoring } from '../dbActions/realtimeScoring';
import { matchCalcs } from '../helpers/match';
import { createInitials } from 'global/common/dataAdjust';

const _allowRealtimeUpdates = true

export const _realtimeMatchTypes = {
  realtimeResults: 'realtimeResults',
  matchDayResults: 'matchDayResults',
  gsResults: 'gsResults',
}

export function useRealtimeUpdate() {

  const appUserAccess = useAppUserAccess()
  const appUserInfo = appUserAccess ? { displayName: appUserAccess.displayName, uid: appUserAccess.uid } : {}
  const { displayName, uid } = appUserInfo ?? {}

  const parentContext = useContext(ParentContext);
  const { states } = parentContext ?? {}
  const { app_state, paps_state } = states
  const { pathViews } = paps_state
  const { app_ts, mainRt } = app_state

  const sportsSeasonContext = useContext(SportsSeasonContext)
  const { sportsSeason_state } = sportsSeasonContext ?? {}
  const { currentSeason_info, sportPermissions, sportDataSettings, matches_info } = sportsSeason_state ?? {}
  const { matches } = matches_info ?? {}
  const { latestSeason } = currentSeason_info ?? {}

  const [updating, setUpdating] = useState()

  const updateMatchScore_realtime = useCallback(async ({ match_rt, match_true, rtType, add }) => {

    setUpdating(true)

    const path_realtime = getMatchPath(false, _realtimeMatchTypes.realtimeResults, pathViews, latestSeason, match_true, uid)

    handleRealTimeChange({ match_rt, match_true, rtType, add })

    matchCalcs.calcVballMatch(match_rt, gameScoringTypes.matchPoints)

    realTimeScoring.calcMatchAndUpdateRtDb(
      app_ts.realtime,
      sportPermissions,
      sportDataSettings,
      match_rt,
      match_true,
      pathViews,
      appUserInfo,
      latestSeason,
      null,
      path_realtime
    )
  }, [])

  const updateMatchResults_matchDay = useCallback(async ({ match_edit, callback }) => {

    const _callback = () => {
      setUpdating(false)
      callback()
    }

    setUpdating(true)

    // this will go to thumbstat
    const { results } = match_edit ?? {}

    const path_match = getMatchPath(false, _realtimeMatchTypes.matchDayResults, pathViews, latestSeason, match_edit, uid)
    const submissionRef = ref(app_ts.realtime, path_match);

    delete results.setNumber

    const submissionData = {
      results: results,
      displayName: displayName ? createInitials(displayName) : 'PUB',
      userName: displayName || 'PUB',
      createdAt: Date.now(),
      status: 'pending',
      origin: 'app'
      // resultType: _matchResultSourceTypes.matchDay
    };

    console.group('updateMatchResults_matchDay', _allowRealtimeUpdates)
    console.log('path_match:', path_match);
    console.log('Submission Request:', submissionData);
    console.groupEnd()

    if (_allowRealtimeUpdates) {
      try {
        await set(submissionRef, submissionData);
        _callback()
        console.log('Submission updated successfully:', submissionData);
      } catch (error) {
        _callback()
        console.error('Error updating submission:', error);
      }
    } else {

      _callback()
    }
  }, [])

  const deleteMatch_realtime = useCallback(async ({ match_rt, removeAll, realtimeMatchType, callback }) => {
    deleteRtScores({
      rtdb: app_ts.realtime,
      realtimeMatchType,
      removeAll,
      pathViews,
      latestSeason,
      matchKeys: [match_rt?._itemKey],
      appUserInfo,
      callback
    })
  }, [])

  const updateMatchResults_realtime = useCallback(async ({ match_rt, match_true }) => {
    const path_realtime = getMatchPath(false, _realtimeMatchTypes.realtimeResults, pathViews, latestSeason, match_true, uid)
    matchCalcs.calcVballMatch(match_rt, gameScoringTypes.matchPoints)
    const { results, setNumber } = match_rt ?? {}
    const { scores, score } = results ?? {}
    const _results = {
      score,
      scores: {}
    }
    _.forEach(scores, (score, setNumber) => {
      _results.scores[`set${setNumber}`] = score
    })
    const updateData = { displayName, setNumber, results: _results };
    console.log('app_ts.realtime', app_ts.realtime)
    console.log('updateMatchResults_realtime', path_realtime, updateData)
    if (_allowRealtimeUpdates) {
      await set(ref(app_ts.realtime, path_realtime), updateData);
    }
  }, [])

  return {
    deleteMatch_realtime,
    updateMatchResults_matchDay,
    updateMatchResults_realtime,
    updateMatchScore_realtime,
    updating
  };
}

const handleRealTimeChange = ({ match_rt, rtType, add, selectedStat }) => {

  let { _results } = match_rt ?? {}

  const { setNumber } = _results ?? {}
  let _setNumber = setNumber ? setNumber : 1

  switch (rtType) {
    case 'away':
    case 'home':
      const sn = setNumber
      if (!_results.scores) { _results.scores = {} }
      if (!_results.scores[sn]) {
        _results.scores[sn] = {
          away: 0,
          home: 0
        }
      }
      let teamScore = _results.scores[sn][rtType]
      if (add) {
        teamScore++
      } else {
        teamScore--
      }
      if (teamScore < 0) { teamScore = 0 }

      _results.scores[setNumber][rtType] = teamScore

      // const _rtTracker = addPoint(rtType, setNumber, match_rt_tracker, selectedStat)
      // handleRtChange_score(teamScore, rtType, _rtTracker)
      break;

    case 'set':
      if (add) {
        _setNumber++
      } else {
        _setNumber--
      }
      if (_setNumber < 1) { teamScore = 1 }
      if (_setNumber > 5) { teamScore = 5 }
      break;

    case 'match':
      _results.final = true
      break;

    default:
      break;
  }
}

const deleteRtScores = async ({
  rtdb,
  removeAll,
  realtimeMatchType,
  appUserInfo,
  latestSeason,
  pathViews,
  matchKeys,
  callback
}) => {
  // /clients/b4C6wHP0A5AgQPTD78ue/events/pojoSports/sports/V8h4h1sJDQpc4Mb969ko/realtimeResults/2027/BU8B9V2vz82Rs9ygUWes/isOUmqKoJGOrd6ncgWMxjClA1Cd2
  // clients/b4C6wHP0A5AgQPTD78ue/events/pojoSports/sports/V8h4h1sJDQpc4Mb969ko/realtimeResults/2027/BU8B9V2vz82Rs9ygUWes/isOUmqKoJGOrd6ncgWMxjClA1Cd2
  const { uid } = appUserInfo ?? {};

  const results = await Promise.all(
    _.map(matchKeys, async (matchKey) => {

      const refString = getMatchPath(
        removeAll,
        realtimeMatchType,
        pathViews,
        latestSeason,
        { _itemKey: matchKey },
        uid
      );

      if (!_allowRealtimeUpdates) {
        console.log('-----------');
        console.log('deleteRealtimeDatabaseScores');
        console.log('_allowRealtimeUpdates', _allowRealtimeUpdates);
        console.log('refString', refString);
        return { success: true };
      }

      try {
        await remove(ref(rtdb, refString));

        return {
          success: true,
          matchKey,
          refString
        };
      } catch (error) {
        return {
          success: false,
          matchKey,
          refString,
          error
        };
      }
    })
  );

  const success = results.every(result => result.success);

  callback?.({
    success,
    results
  });
};

const getMatchPath = (forAll, scoreType, pathViews, latestSeason, match_true, uid) => {
  const path = `clients/${pathViews.clients}/events/${pathViews.events}/${scoreType}/${latestSeason}/`;
  if (forAll) {
    return path
  } else {
    if (uid) {
      return `${path}${match_true?._itemKey}/${uid}`
    } else {
      return `${path}${match_true?._itemKey}`
    }
  }
}

const addPoint = (team, setNumber, match_rt_tracker, selectedStat) => {

  const _rt_tracker = _.cloneDeep(match_rt_tracker);
  const { largestDifference, score, streaks, pointHistory } = match_rt_tracker ?? {}

  const newScore = _.cloneDeep(score);
  newScore[team] += 1;

  const point = pointHistory.length + 1;
  const newPoint = { point, winner: team, ..._.cloneDeep(newScore), setNumber, selectedStat };
  const newPointHistory = [...pointHistory, newPoint];

  const newStreaks = _.cloneDeep(streaks);
  if (last(pointHistory)?.winner === team) {
    newStreaks[team] += 1;
  } else {
    newStreaks[team] = 1;
    newStreaks[team === "home" ? "away" : "home"] = 0;
  }

  const currentDifference = Math.abs(newScore.home - newScore.away);
  const newLargestDifference = _.cloneDeep(largestDifference);
  if (team === "home" && newScore.home > newScore.away) {
    newLargestDifference.home = Math.max(newLargestDifference.home, currentDifference);
  } else if (team === "away" && newScore.away > newScore.home) {
    newLargestDifference.away = Math.max(newLargestDifference.away, currentDifference);
  }

  _rt_tracker.streaks = newStreaks
  _rt_tracker.largestDifference = newLargestDifference
  _rt_tracker.score = newScore
  _rt_tracker.pointHistory = newPointHistory

  return _rt_tracker

};
